import {
  Box,
  Button,
  Container,
  Drawer,
  Flex,
  Menu,
  Select,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import store from "../../../store";
import { setTasks } from "../../../store/reducers/task";
import TaskTable from "../../../components/TaskTable";
import CustomPagination from "../../../components/Pagination/CustomPagination";
import AddTaskModal from "../../../components/TaskTable/AddTaskModal";
import AddTask from "./AddTask";
import { getTasks, getTaskTypes } from "../../../services/task";
import { getAllFacilitiesModal } from "../../../services";
import { getAllStaff } from "../../../services/staff";
import { getAllPatientsModal } from "../../../services/patient";
import { getAllSignatures } from "../../../services/signature";
import { IoChevronBack, IoFilter } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { DatePickerInput } from "@mantine/dates";
import moment from "moment";
import useStyles from "../Note/style";
import { NewAddButton } from "../../../components/AddButton/NewAddButton";

const ClientTaskView = ({ userId, activeTab }) => {
  const { classes } = useStyles();

  const [currentPage, setCurrentPage] = useState(1);
  const { tasksCount, taskTypes } = useSelector((state) => state?.task);
  const { allDeletedFacilities } = useSelector((state) => state?.facility);
  const { allStaff } = useSelector((state) => state?.staff);
  const [opened, { open, close }] = useDisclosure(false);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [selectedTaskType, setSelectedTaskType] = useState(null);
  const [selectedTaskTypeName, setSelectedTaskTypeName] = useState(null);

  const handleTaskTypeSelected = ({ taskType, taskTypeName }) => {
    setSelectedTaskType(taskType);
    setSelectedTaskTypeName(taskTypeName);
    openDrawer();
  };

  const handleTaskComplete = () => {
    closeDrawer();
    setSelectedTaskType(null);
    setSelectedTaskTypeName(null);
    getTasks(1, "all", { ...activeFilters, client: userId });
  };

  // Filter state (no client filter — already scoped to userId)
  const [activeFilters, setActiveFilters] = useState({
    taskType: null,
    facility: null,
    staffFilter: null,
    dateRange: null,
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [tempTaskType, setTempTaskType] = useState(null);
  const [tempFacility, setTempFacility] = useState(null);
  const [tempStaff, setTempStaff] = useState(null);
  const [dateRangeVal, setDateRangeVal] = useState([]);
  const [oneDayValue, setOneDayValue] = useState(null);

  useEffect(() => {
    if (activeTab === "tasks") {
      getTaskTypes("all");
      getAllFacilitiesModal("withDeleted");
      getAllStaff("all");
      getAllPatientsModal();
      getAllSignatures();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "tasks") {
      store.dispatch(setTasks({ data: [], count: 0 }));
      getTasks(currentPage, "all", { ...activeFilters, client: userId });
    }
  }, [currentPage, activeFilters, activeTab]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Filter helpers
  const hasActiveFilters =
    activeFilters.taskType ||
    activeFilters.facility ||
    activeFilters.staffFilter ||
    activeFilters.dateRange;

  const getTaskTypeName = (id) => {
    const tt = taskTypes?.find((t) => t.id === id);
    return tt?.name || id;
  };

  const getFacilityName = (id) => {
    const fac = allDeletedFacilities?.facilities?.find((f) => f.id === id);
    return fac?.name || id;
  };

  const getStaffName = (id) => {
    const staff = allStaff?.find((s) => s.id === id);
    return (
      staff?.name ||
      `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim() ||
      id
    );
  };

  const handleDateRange = (dates) => {
    if (dates[0] == null && dates[1] == null) {
      setDateRangeVal([]);
    } else {
      setDateRangeVal(dates);
    }
  };

  const handleDateChange = (date) => {
    setOneDayValue(date);
  };

  const handleApplyFilter = () => {
    const newFilters = { ...activeFilters };
    if (selectedCategory === "dateRange") {
      if (dateRangeVal?.length && dateRangeVal[0] && dateRangeVal[1]) {
        const startDate = moment(dateRangeVal[0]).startOf("day");
        const endDate = moment(dateRangeVal[1]).endOf("day");
        newFilters.dateRange = `${startDate.toISOString()},${endDate.toISOString()}`;
      } else if (oneDayValue) {
        const startDate = moment(oneDayValue).startOf("day");
        const endDate = moment(oneDayValue).endOf("day");
        newFilters.dateRange = `${startDate.toISOString()},${endDate.toISOString()}`;
      }
    }
    if (selectedCategory === "facility" && tempFacility) {
      newFilters.facility = tempFacility;
    }
    if (selectedCategory === "taskType" && tempTaskType) {
      newFilters.taskType = tempTaskType;
    }
    if (selectedCategory === "staff" && tempStaff) {
      newFilters.staffFilter = tempStaff;
    }
    setActiveFilters(newFilters);
    setCurrentPage(1);
    setFilterDropdownOpen(false);
    setSelectedCategory(null);
  };

  const handleResetAllFilters = () => {
    setActiveFilters({
      taskType: null,
      facility: null,
      staffFilter: null,
      dateRange: null,
    });
    setDateRangeVal([]);
    setOneDayValue(null);
    setTempTaskType(null);
    setTempFacility(null);
    setTempStaff(null);
    setSelectedCategory(null);
    setCurrentPage(1);
    setFilterDropdownOpen(false);
  };

  const handleRemoveFilter = (key) => {
    if (key === "dateRange") {
      setDateRangeVal([]);
      setOneDayValue(null);
    }
    setActiveFilters((prev) => ({ ...prev, [key]: null }));
    setCurrentPage(1);
  };

  const formatDateRange = (dr) => {
    if (!dr) return "";
    const [start, end] = dr.split(",");
    return `${moment(start).format("MM/DD/YY")} - ${moment(end).format(
      "MM/DD/YY"
    )}`;
  };

  return (
    <Container fluid className={classes.content}>
      <AddTaskModal
        opened={opened}
        onclose={() => close()}
        title={"Add a New Task"}
        clientId={userId}
        onTaskTypeSelected={handleTaskTypeSelected}
      />
      <Drawer
        opened={drawerOpened}
        onClose={handleTaskComplete}
        position="right"
        size="85%"
        title="Add Task"
        padding="md"
        zIndex={1000}
      >
        {drawerOpened && (
          <AddTask
            embedded
            embeddedClientId={userId}
            embeddedTaskType={selectedTaskType}
            embeddedTaskTypeName={selectedTaskTypeName}
            onComplete={handleTaskComplete}
          />
        )}
      </Drawer>

      {/* Header */}
      <Box className={classes.btn}>
        <Flex align={"center"} gap={10}>
          <Text className={classes.titleText}>All Tasks</Text>
        </Flex>
        <Box className={classes.rightBTN}>
          {/* Filter */}
          <Menu
            width={Math.min(350, window.innerWidth - 24)}
            shadow="md"
            radius={"md"}
            position="bottom-end"
            opened={filterDropdownOpen}
            classNames={{ dropdown: classes.notesFilterBox }}
            onClose={() => {
              setFilterDropdownOpen(false);
              setSelectedCategory(null);
            }}
          >
            <Menu.Target title="Filter">
              <Button
                size="md"
                radius="md"
                title="Filter"
                leftIcon={<IoFilter />}
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                color="gray"
                variant="outline"
              >
                Filter
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              {!selectedCategory ? (
                <>
                  <Box className="header">
                    <Text>Filter</Text>
                    <Button
                      variant="unstyled"
                      onClick={() => setFilterDropdownOpen(false)}
                    >
                      <RxCross2 size={24} />
                    </Button>
                  </Box>
                  <Box className={classes.filterCategoryList}>
                    <Box
                      className={classes.filterCategoryItem}
                      onClick={() => setSelectedCategory("dateRange")}
                    >
                      <Text>Date Range</Text>
                      {activeFilters.dateRange && (
                        <Box className={classes.activeFilterDot} />
                      )}
                    </Box>
                    <Box
                      className={classes.filterCategoryItem}
                      onClick={() => {
                        setSelectedCategory("facility");
                        setTempFacility(activeFilters.facility);
                      }}
                    >
                      <Text>Facility Name</Text>
                      {activeFilters.facility && (
                        <Box className={classes.activeFilterDot} />
                      )}
                    </Box>
                    <Box
                      className={classes.filterCategoryItem}
                      onClick={() => {
                        setSelectedCategory("taskType");
                        setTempTaskType(activeFilters.taskType);
                      }}
                    >
                      <Text>Task Type</Text>
                      {activeFilters.taskType && (
                        <Box className={classes.activeFilterDot} />
                      )}
                    </Box>
                    <Box
                      className={classes.filterCategoryItem}
                      onClick={() => {
                        setSelectedCategory("staff");
                        setTempStaff(activeFilters.staffFilter);
                      }}
                    >
                      <Text>Staff Name</Text>
                      {activeFilters.staffFilter && (
                        <Box className={classes.activeFilterDot} />
                      )}
                    </Box>
                  </Box>
                  <Box className="boxFooter">
                    <Button
                      variant="outline"
                      onClick={handleResetAllFilters}
                      className="resetBtn"
                    >
                      Reset All
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    className={classes.filterCategoryHeader}
                    onClick={() => setSelectedCategory(null)}
                  >
                    <IoChevronBack size={16} />
                    <Text weight={600}>
                      {selectedCategory === "dateRange" && "Date Range"}
                      {selectedCategory === "facility" && "Facility Name"}
                      {selectedCategory === "taskType" && "Task Type"}
                      {selectedCategory === "staff" && "Staff Name"}
                    </Text>
                  </Box>
                  <Box className="filterBody">
                    {selectedCategory === "dateRange" && (
                      <>
                        <Box className="fieldParent">
                          <DatePickerInput
                            size="sm"
                            radius={"md"}
                            clearable
                            type="range"
                            value={dateRangeVal}
                            disabled={oneDayValue}
                            className="datePicker"
                            label="Select Date Range"
                            onChange={handleDateRange}
                            placeholder="Select Date Range"
                          />
                        </Box>
                        <Box className="fieldParent">
                          <DatePickerInput
                            radius={"md"}
                            clearable
                            size="sm"
                            className="datePicker"
                            value={oneDayValue}
                            label="Select Date"
                            placeholder="Select Date"
                            onChange={handleDateChange}
                            disabled={dateRangeVal?.length}
                          />
                        </Box>
                      </>
                    )}
                    {selectedCategory === "facility" && (
                      <Box className="fieldParent">
                        <Select
                          label="Select Facility"
                          placeholder="Search facility..."
                          searchable
                          clearable
                          value={tempFacility}
                          onChange={setTempFacility}
                          data={
                            allDeletedFacilities?.facilities
                             ?.filter(
                                (p) => p?.active === true && p?.deleted === false,
                              )
                            ?.map((f) => ({
                              label: f?.name,
                              value: f?.id,
                            })) || []
                          }
                        />
                      </Box>
                    )}
                    {selectedCategory === "taskType" && (
                      <Box className="fieldParent">
                        <Select
                          label="Select Task Type"
                          placeholder="Search task type..."
                          searchable
                          clearable
                          value={tempTaskType}
                          onChange={setTempTaskType}
                          data={
                            taskTypes?.map((t) => ({
                              label: t.name,
                              value: t.id,
                            })) || []
                          }
                        />
                      </Box>
                    )}
                    {selectedCategory === "staff" && (
                      <Box className="fieldParent">
                        <Select
                          label="Select Staff"
                          placeholder="Search staff..."
                          searchable
                          clearable
                          value={tempStaff}
                          onChange={setTempStaff}
                          data={
                            allStaff
                             ?.filter(
                                (p) => p?.active === true && p?.deleted === false,
                              )
                            ?.map((s) => ({
                              label:
                                s?.name ||
                                `${s?.firstName || ""} ${
                                  s?.lastName || ""
                                }`.trim(),
                              value: s.id,
                            })) || []
                          }
                        />
                      </Box>
                    )}
                  </Box>
                  <Box className="boxFooter">
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (selectedCategory === "dateRange") {
                          setDateRangeVal([]);
                          setOneDayValue(null);
                        }
                        if (selectedCategory === "facility")
                          setTempFacility(null);
                        if (selectedCategory === "taskType")
                          setTempTaskType(null);
                        if (selectedCategory === "staff") setTempStaff(null);
                      }}
                      className="resetBtn"
                    >
                      Reset
                    </Button>
                    <Button className="applyBtn" onClick={handleApplyFilter}>
                      Apply
                    </Button>
                  </Box>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
          <NewAddButton
            title="Add Task"
            onClick={open}
            color="#FFAF4A"
            textColor="#000"
            variant="filled"
            borderColor="none"
          />
        </Box>
      </Box>

      {/* Active Filter Chips */}
      {hasActiveFilters && (
        <Box className={classes.activeFiltersBar}>
          {activeFilters.dateRange && (
            <Box className={classes.filterChip}>
              <Text size="xs">
                Date: {formatDateRange(activeFilters.dateRange)}
              </Text>
              <RxCross2
                size={12}
                onClick={() => handleRemoveFilter("dateRange")}
              />
            </Box>
          )}
          {activeFilters.facility && (
            <Box className={classes.filterChip}>
              <Text size="xs">
                Facility: {getFacilityName(activeFilters.facility)}
              </Text>
              <RxCross2
                size={12}
                onClick={() => handleRemoveFilter("facility")}
              />
            </Box>
          )}
          {activeFilters.taskType && (
            <Box className={classes.filterChip}>
              <Text size="xs">
                Task Type: {getTaskTypeName(activeFilters.taskType)}
              </Text>
              <RxCross2
                size={12}
                onClick={() => handleRemoveFilter("taskType")}
              />
            </Box>
          )}
          {activeFilters.staffFilter && (
            <Box className={classes.filterChip}>
              <Text size="xs">
                Staff: {getStaffName(activeFilters.staffFilter)}
              </Text>
              <RxCross2
                size={12}
                onClick={() => handleRemoveFilter("staffFilter")}
              />
            </Box>
          )}
          <Text className={classes.clearAllLink} onClick={handleResetAllFilters}>
            Clear all
          </Text>
        </Box>
      )}

      {/* Task Table */}
      <Box className={classes.table}>
        <Box>
          <TaskTable currentPage={currentPage} isClientPage={true} clientId={userId} />
        </Box>
        <CustomPagination
          data={tasksCount}
          itemsPerPage={6}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      </Box>
    </Container>
  );
};

export default ClientTaskView;
